import CONF from 'config/conf.singleton';
import ENV from 'config/env.conf';
import MS from 'constants/time/ms.const';
import { FeStack } from 'errors/fe.error';
import fromAnyToTrimmed from 'utils/converters/any/fromAnyToTrimmed.util';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';
import { fromAnyDateToZulu } from 'utils/converters/time/from.util';
import { locationReload } from 'utils/dom/location.util';
import { isEnvDev, isEnvProd } from 'utils/env/determine.util';
import delayed from 'utils/functions/delayed.util';
import alwaysNoop from 'utils/functions/nullary/alwaysNoop.util';
import nullBind from 'utils/functions/nullBind.util';
import isNotMissing from 'utils/predicates/any/isNotMissing.util';
import isNotFn from 'utils/predicates/function/isNotFn.util';
import isPositiveInteger from 'utils/predicates/number/isPositiveInteger.util';
import { isTimeEqual, isTimeNotBefore } from 'utils/predicates/time/compare.util';
import isDateWorthy from 'utils/predicates/time/isDateWorthy.util';

const CTAG = '[RELEASE]';
const CTAG2 = '[INIT-RELATED]';

const logW = /**@type VariadicV*/ console.warn.bind(console, '[W]', CTAG, CTAG2); // eslint-disable-line no-console,no-unused-vars
const logI = /**@type VariadicV*/ console.info.bind(console, '[I]', CTAG, CTAG2); // eslint-disable-line no-console,no-unused-vars
const logD = /**@type VariadicV*/ console.log.bind(console, '[D]', CTAG, CTAG2); // eslint-disable-line no-console,no-unused-vars

const MIN_TIMEOUT = MS.minute;

// NOTE: these are module level mutable values that act as the local state of the RELEASE singleton
let fetchPromise = null;
let fetchValue = '';
let mockData = null;
let timeout = isEnvDev() ? MS.minute : isEnvProd() ? MS.hour : 5 * MS.minute; // TODO: use environment variables for these values

const listeners = new Set();

const invokeListeners = () => {
  for (const fn of [...listeners.values()]) {
    try {
      fn(RELEASE);
    } catch (e) {
      logW('invokeListeners()', e);
    }
  }
};

const unsubscribe = (fn) => void listeners?.delete?.(fn);

const subscribe = (fn) => {
  if (isNotFn(fn)) {
    logW('subscribe()', 'Invalid function:', fn);
    return alwaysNoop;
  }

  listeners?.add?.(fn);
  fn(RELEASE);
  return nullBind(unsubscribe, fn);
};

const mock = (data) => {
  if (isNotMissing(data)) mockData = data;

  invokeListeners();
  return mockData;
};

const fetchBuild = async () => {
  const FTAG = 'fetchBuild()';
  try {
    const base = ENV.PUBLIC_URL;
    const name = `build.json`;
    const url = `${base}/${name}`;

    logI(FTAG, 'Fetching release from:', name, 'at:', base, 'with url:', url, '...');

    const response = await fetch(url);
    const json = await response.json();

    logI(FTAG, 'Done. The new build json:', json);

    return json;
  } catch (e) {
    if (isEnvDev()) {
      logD(FTAG, 'Error message:', e.message);
    } else {
      // noinspection JSVoidFunctionReturnValueUsed
      throw logW(FTAG, e);
    }
  }
};

const build = () => (mockData?.build ?? CONF.buildCiNum) || 'missing build number';
const currSha = () => fromAnyToTrimmed(mockData?.buildShaLong) || fromAnyToTrimmed(CONF.buildShaLong);
const currNow = () => fromAnyToTrimmed(mockData?.buildNow) || fromAnyToTrimmed(CONF.buildNow);

const nextFull = async (options) => {
  try {
    if (!options?.force && mockData) return mockData;
    if (!options?.force && fetchValue) return fetchValue;

    // noinspection ES6MissingAwait
    fetchPromise = fetchPromise ?? fetchBuild();
    const json = await fetchPromise;
    fetchPromise = null;

    if (json) fetchValue = json;

    return json;
  } catch (e) {
    logW('nextFull()', e);
    return null;
  }
};

const nextSha = async (options) => {
  try {
    const build = await nextFull(options);
    return fromAnyToTrimmed(build?.SHA_LONG);
  } catch (e) {
    logW('nextSha()', e);
    return '';
  }
};

const nextNow = async (options) => {
  try {
    const build = await nextFull(options);
    return fromAnyToTrimmed(build?.NOW);
  } catch (e) {
    logW('nextNow()', e);
    return '';
  }
};

export const determineIfIsUpToDate = (options) => {
  const FTAG = 'determineIfIsUpToDate()';

  // NOTE: using `before` acts like using `force`
  const before = fromAnyDateToZulu(options?.before);

  const beNow = fromAnyDateToZulu(options?.beNow);
  const feNow = fromAnyDateToZulu(options?.feNow);
  const isSameSha = options?.beSha === options?.feSha;

  if (!options?.before) {
    const result = fromAnyToBool(!options?.feSha || !options?.beSha || isSameSha);
    if (false === result) logI('Code is not up to date because of these options:', options);
    return result;
  }

  if (!isDateWorthy(before)) {
    logW(FTAG, 'Not a good date before:', before);
    return true;
  }

  if (!isDateWorthy(feNow)) {
    logW(FTAG, 'Not a good date feNow:', feNow);
    return true;
  }

  if (!isDateWorthy(beNow)) {
    logW(FTAG, 'Not a good date beNow:', beNow);
    return true;
  }

  if (isTimeEqual(beNow, feNow)) return true;
  if (isTimeNotBefore(feNow, beNow)) return true;

  const timeBeNotBeforeLimit = isTimeNotBefore(beNow, before);
  const timeFeNotBeforeBe = isTimeNotBefore(feNow, beNow);

  const result = timeBeNotBeforeLimit && timeFeNotBeforeBe;
  if (false === result) {
    const reasons = { timeBeNotBeforeLimit, timeFeNotBeforeBe, beNow, feNow, before };
    logI('Code is not up to date because of:', reasons);
  }

  return result;
};

const isUpToDate = async (options) => {
  try {
    const full = await nextFull({ force: options?.force });
    return determineIfIsUpToDate({
      before: options?.before,
      beNow: full?.NOW,
      feNow: currNow(),
      beSha: full?.SHA_LONG,
      feSha: currSha(),
    });
  } catch (e) {
    logW('isUpToDate()', e);
    return true;
  }
};

const display = async () => {
  const b = build();
  const isCurrent = await isUpToDate();

  return isCurrent ? b : `${b} (new version is available)`;
};

const reload = ($) => void locationReload(new FeStack({ tag: fromAnyToTrimmed($?.tag) || 'RELEASE__reload' }));

const interval = (options) => {
  if (void 1 === options) return timeout;

  const valid = isPositiveInteger(options?.timeout) && options?.timeout >= MIN_TIMEOUT;
  if (!valid) logW('interval()', 'Invalid value for timeout:', options?.timeout);

  timeout = valid ? options?.timeout : timeout;
  return timeout;
};

const loop = () =>
  void nextFull({ force: true })
    .then(() => {
      invokeListeners();
      delayed(timeout, loop); // repeat
    })
    .catch((e) => {
      logW('loop()', e);
    });

loop(); // kick off the loop

const RELEASE = {
  mock, //
  //
  build,
  currNow,
  currSha,
  nextSha,
  nextNow,
  nextFull, //
  //
  display,
  interval,
  isUpToDate,
  reload, //
  //
  subscribe,
  unsubscribe,
};

export default RELEASE;
